<template>
  <div class="Milestones" v-if="milestonesOrFixture">
    <container class="pt-0 double-padding-bottom">
      <ScrollXNavbarCmp>
        <template>
          <li
            v-for="item in milestonesOrFixture"
            :key="`tab-${item.id}-${_uid}`"
            :class="['tab', {'active': item.id === activeTab.id}]"
            :id="`tab-${item.id}`"
            @click="onTabClick(item.id)"
          >
            <span class="tab_title text text-24">{{ item.tab_title }}</span>
            <span class="period text text-14 text-extra-bold uppercase">{{ item.period }}</span>
          </li>
        </template>
      </ScrollXNavbarCmp>
    </container>
    <div class="wrapper">
      <div class="h-line"></div>
      <div class="hero">
        <container class="py-0">
          <div class="row justify-content-center">
            <div class="col-12 col-md-7 col-xl-6">
              <title-text>
                <span slot="over-title" class="over-title uppercase">{{ active_tab.period }}</span>
                <h3 slot="title" class="title title-32" v-html="active_tab.header"></h3>
              </title-text>
            </div>
          </div>
        </container>
        <img
          :src="active_tab.image.src ? active_tab.image.src : '//placehold.it/1440x630'" :alt="active_tab.image.alt"
          class="image resp-image"
        >
      </div>
      <container
        class="double-padding-top"
        :class="nextTab ? 'double-padding-bottom' : 'pb-0'"
      >
        <postit-on-image
          v-for="(item, i) in active_tab.items"
          :key="`milestone-${i}-${_uid}`"
          class="chained reverse postit-on-top-mobile"
        >
          <template slot="postit">
            <title-text class="text-align-left over-title-separator">
              <span slot="over-title" class="over-title uppercase">{{ item.year }}</span>
              <h3 slot="title" class="title title-32 mb-0">{{ item.title }}</h3>
              <p slot="text" class="text text-20" v-html="item.description"></p>
            </title-text>
            <p class="pt-4" v-if="item.link">
              <icon-link :href="item.link.url" :icon="item.link.external ? 'arrow-top-right' : 'arrow-right'" :target="item.link.external ? '_blank' : '_self'" :rel="item.link.external ? 'noopener' : false">{{ item.link.text }}</icon-link>
            </p>
          </template>
          <img
            slot="content"
            :src="item.image.src ? item.image.src : '//placehold.it/732x400'" :alt="item.image.alt"
            class="image resp-image"
          >
        </postit-on-image>
      </container>
    </div>
    <div class="hero next-tab" v-if="nextTab">
      <container class="py-0">
        <div class="row justify-content-center">
          <div class="col-12 col-md-7 col-xl-6">
            <title-text>
              <span slot="over-title" class="over-title uppercase">{{ next_tab.period }}</span>
              <h3 slot="title" class="title title-32" v-html="next_tab.header"></h3>
            </title-text>
            <div class="mt-4 text-align-center">
              <btn class="blue-lochmara-ghost" tag="button" @click.native="onTabClick(nextTab.id, true)">Show more</btn>
            </div>
          </div>
        </div>
      </container>
      <div class="image-wrapper">
        <img
          :src="next_tab.image.src ? next_tab.image.src : '//placehold.it/1440x630'" :alt="next_tab.image.alt"
          class="image resp-image"
        >
      </div>
    </div>
  </div>
</template>

<script>
  import Container from '@components/layout/container';
  import TitleText from '@components/title-text';
  import Separator from '@components/layout/separator';
  import PostitOnImage from '@components/layout/postit-on-image';
  import IconLink from '@components/atoms/icon-link';
  import Btn from '@components/atoms/btn';
  import ScrollXNavbarCmp from '@components/scroll-x-navbar-cmp';

  export default {
    name: 'Milestones',
    components: {ScrollXNavbarCmp, Btn, IconLink, PostitOnImage, Separator, TitleText, Container},
    props: {
      milestones: {
        type: Array
      }
    },
    data() {
      return {
        active_tab: null,
        next_tab: null
      };
    },
    asyncComputed: {
      async milestonesOrFixture() {
        return this.milestones ? this.milestones : (await import('@fixtures/milestones-fixture')).default;
      }
    },
    computed: {
      activeTab() {
        if (!this.active_tab && this.milestonesOrFixture) {
          this.active_tab = this.milestonesOrFixture[0];
        }
        return this.active_tab;
      },
      nextTab() {
        if (this.active_tab && this.milestonesOrFixture) {
          const index = this.milestonesOrFixture.findIndex(e => e.id === this.active_tab.id);
          if (index !== -1) {
            if ((index + 1) >= this.milestonesOrFixture.length) {
              this.next_tab = null;
            } else {
              this.next_tab = this.milestonesOrFixture[index + 1];
            }
          } else {
            this.next_tab = null;
          }
        }
        return this.next_tab;
      }
    },
    methods: {
      onTabClick(id, scrollTo = false) {
        if (scrollTo) {
          this.$scrollTo(`#tab-${id}`, 500, {
            cancelable: false,
            easing: 'ease',
            offset: -250
          });
        }
        const index = this.milestonesOrFixture.findIndex(e => e.id === id);
        if (index !== -1) {
          this.active_tab = this.milestonesOrFixture[index];
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '~$scss/variables';
  @import '~$scss/utility';

  .Milestones {
    @include responsive-property(padding-top, $section-padding);
    @include responsive-property(padding-bottom, $section-padding);

    // Theme: double-padding-top to be used when previous block has colored bg
    &.double-padding-top {
      @include responsive-property(padding-top, $section-double-padding);
    }

    // Theme: double-padding-bottom to be used when next block has colored bg
    &.double-padding-bottom {
      @include responsive-property(padding-bottom, $section-double-padding);
    }

    // Theme: half-padding-top
    &.half-padding-top {
      @include responsive-property(padding-top, $section-half-padding);
    }

    // Theme: half-padding-bottom
    &.half-padding-bottom {
      @include responsive-property(padding-bottom, $section-half-padding);
    }

    // Theme: last-section-padding-bottom
    &.last-section-padding-bottom {
      @include responsive-property(padding-bottom, $last-section-padding);
    }

    // Theme: first-section-padding-top
    &.first-section-padding-top {
      @include responsive-property(padding-top, $last-section-padding);
    }

    // Theme: regular-padding-top
    &.regular-padding-top {
      @include responsive-property(padding-top, $section-regular-padding);
    }

    // Theme: regular-padding-bottom
    &.regular-padding-bottom {
      @include responsive-property(padding-bottom, $section-regular-padding);
    }

    .wrapper {
      position: relative;

      .h-line {
        position: absolute;
        top: 0;
        left: 50%;
        bottom: 0;
        width: 1px;
        background: $color-blue;

        @media (min-width: $bootstrap-md) {
          left: 20%;
        }
      }
    }

    .hero {
      position: relative;
      padding: 175px 0 0;

      @media (min-width: $bootstrap-sm) {
        padding: 130px 0 0;
      }

      @media (min-width: $bootstrap-md) {
        padding: 45px 0 0;
      }

      .ContainerBlock {
        position: absolute;
        top: 0;
        width: 100%;
        padding-bottom: 40px !important;
        background: rgb(255, 255, 255);
        background: linear-gradient(180deg, rgba(255, 255, 255, 1) 90%, rgba(255, 255, 255, 0) 100%);
        z-index: 1 !important;

        @media (min-width: $bootstrap-md) {
          background: linear-gradient(180deg, rgba(255, 255, 255, 1) 20%, rgba(255, 255, 255, 0) 100%);
        }
      }

      ::v-deep .title {
        min-height: 102px;

        @media (min-width: $bootstrap-sm) {
          min-height: 68px;
        }
      }

      &.next-tab {
        height: 320px;
        overflow: hidden;

        .image-wrapper {
          position: relative;

          &:after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: rgba(255, 255, 255, .66);
          }
        }
      }
    }
  }
</style>
